import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronsUpDown, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export type MultiSelectOption = {
  label: string;
  value: string;
};

type Props = {
  options: MultiSelectOption[];
  // legacy API (simple <select>)
  value?: string[];
  onChange?: (vals: string[]) => void;
  // modern API (rich popover)
  selectedValues?: string[];
  onSelectionChange?: (vals: string[]) => void;
  // UI options
  placeholder?: string;
  searchPlaceholder?: string;
  testId?: string;
  // if provided and equals 'legacy', render the simple <select> UI
  className?: string;
};

export function MultiSelectFilter(props: Props) {
  const {
    options,
    value,
    onChange,
    selectedValues,
    onSelectionChange,
    placeholder = 'Select items',
    searchPlaceholder = 'Search...',
    testId,
    className,
  } = props;

  // Support both APIs: prefer modern props but fall back to legacy ones
  const currentSelected = selectedValues ?? value ?? [];
  const setSelected = (vals: string[]) => {
    if (onSelectionChange) onSelectionChange(vals);
    if (onChange) onChange(vals);
  };

  // If caller explicitly asked for legacy select (className contains 'legacy'), render simple select
  if (className && className.includes('legacy')) {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selected = Array.from(e.target.selectedOptions).map(o => o.value);
      setSelected(selected);
    };

    return (
      <select
        multiple
        value={currentSelected}
        onChange={handleChange}
        className={className ?? 'border rounded p-2'}
        data-testid={testId}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }

  // Modern rich popover UI
  const [open, setOpen] = useState(false);

  const toggleValue = (val: string) => {
    const newValues = currentSelected.includes(val)
      ? currentSelected.filter(v => v !== val)
      : [...currentSelected, val];
    setSelected(newValues);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected([]);
  };

  const selectedCount = currentSelected.length;
  const displayText = selectedCount === 0
    ? placeholder
    : selectedCount === 1
      ? options.find(o => o.value === currentSelected[0])?.label || placeholder
      : `${selectedCount} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          data-testid={testId}
        >
          <span className="truncate">{displayText}</span>
          <div className="flex items-center gap-1">
            {selectedCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-2 h-5 px-1 text-xs"
                onClick={clearAll}
              >
                {selectedCount}
                <X className="ml-1 h-3 w-3" />
              </Badge>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>No items found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = currentSelected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggleValue(option.value)}
                    data-testid={testId ? `${testId}-item-${option.value}` : undefined}
                  >
                    <Checkbox
                      checked={isSelected}
                      className="mr-2"
                      onCheckedChange={() => toggleValue(option.value)}
                    />
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelectFilter;
